// frontend/src/features/ups/BannedDriversList.js
// This React component renders the UPS banned drivers table, honoring global agency/search filters,
// live-syncing via Socket.IO, and letting admins unban drivers or view/edit ban details in a modal.

// Phase 6 migration:
// - READ: driverSlice('Banned') -> bansSlice read-model
// - REALTIME: remove component socket patching (Option A = central refetch in socket.js)
// - WRITES: keep unban API call; after success, refresh bans list

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { UserCheck, Eye } from 'lucide-react';

import { selectIsUpsAdmin } from '../auth/authSlice';
import { selectAllAgencies } from '../agencies/agenciesSlice';
import { selectFilters, bumpStatsRefreshKey } from '../ui/filtersSlice';

import { unbanDriver } from '../../services/upsDriverService';
import BannedDriverEditModal from './BannedDriverEditModal';

// ✅ New read model (adjust export names if your slice differs)
import {
  loadBannedDrivers,
  selectBannedRows,
  selectBansLoading,
  selectBansError,
} from '../bans/bansSlice';

export default function BannedDriversList() {
  const dispatch = useDispatch();
  const isAdmin  = useSelector(selectIsUpsAdmin);

  // Global filters/state
  const { agencyId, q } = useSelector(selectFilters);
  const agencies        = useSelector(selectAllAgencies);

  // ✅ Read from bans slice (source of truth)
  const drivers = useSelector(selectBannedRows);
  const loading = useSelector(selectBansLoading);
  const error   = useSelector(selectBansError);

  // ✅ Load bans on mount + whenever filters change
  // If your API supports filtering, pass { agencyId, q } to the thunk.
  useEffect(() => {
    dispatch(loadBannedDrivers({ agencyId, q }));
  }, [dispatch, agencyId, q]);

  // Focus-on-arrival via ?focus=<id>
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const focus = sp.get('focus');
    if (!focus) return;
    const el = document.querySelector(`[data-row-id="${focus}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ring-2','ring-blue-400','animate-pulse');
    const t = setTimeout(() => {
      el.classList.remove('ring-2','ring-blue-400','animate-pulse');
      const next = new URLSearchParams(sp);
      next.delete('focus');
      setSp(next, { replace: true });
    }, 1600);
    return () => clearTimeout(t);
  }, [sp, setSp]);

  // Modal state
  const [showEdit, setShowEdit] = useState(false);
  const [selectedBan, setSelectedBan] = useState(null);

  // Actions
  const handleUnban = async row => {
    try {
      // Prefer ban_id; backend may accept driver_id fallback but ban_id is correct
      const idForApi = row.ban_id ?? row.id;
      await unbanDriver(idForApi);

      // ✅ Option A: refresh the banned list after unban so it disappears
      dispatch(loadBannedDrivers({ agencyId, q }));

      dispatch(bumpStatsRefreshKey()); // ✅ NEW: force UpsDriverStats to recompute counts
      // NOTE: Active/Reserved refresh is handled by socket.js on driverUnbanned
    } catch (err) {
      console.error('Unban failed:', err);
    }
  };

  // Modal helpers
  const openEdit  = row => { setSelectedBan(row); setShowEdit(true); };
  const closeEdit = () => { setShowEdit(false); setSelectedBan(null); };

  // After updating ban details (reason/scope), refresh bans list (Option A)
  const handleBanUpdated = () => {
    dispatch(loadBannedDrivers({ agencyId, q }));
  };

  // Empty-state labels
  const scopeLabel = (() => {
    if (!agencyId) return 'All Agencies';
    const a = (agencies || []).find(
      x => String(x.id ?? x.agency_id ?? x.agencyId) === String(agencyId)
    );
    return a?.name ?? a?.agency_name ?? `Agency #${agencyId}`;
  })();
  const hasSearch = (q || '').trim().length > 0;

  return (
    <>
      <div className="overflow-x-auto my-6">
        <h3 className="text-2xl font-bold mb-4 text-gray-800">Banned Drivers</h3>

        {/* Optional: show error banner */}
        {error && (
          <div className="mb-4 text-sm text-red-700 bg-red-100 p-3 rounded">
            {error}
          </div>
        )}

        <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Name','Vehicle Reg.','Phone','Email','Agency','Banned At','Actions'].map(col => (
                <th key={col} className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  Loading banned drivers…
                </td>
              </tr>
            ) : (drivers || []).length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No banned drivers{agencyId ? ` for ${scopeLabel}` : ''}{hasSearch ? ` matching “${q}”` : ''}.
                </td>
              </tr>
            ) : (drivers || []).map(driver => (
              <tr key={driver.id} data-row-id={driver.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">{driver.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {driver.vehicle_registration_number || driver.vehicleRegistrationNumber || driver.vrn || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{driver.phone}</td>
                <td className="px-6 py-4 whitespace-nowrap">{driver.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {driver.agency_name || driver.agency?.name || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {driver.banned_at ? new Date(driver.banned_at).toLocaleString() : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => handleUnban(driver)}
                      disabled={!isAdmin}
                      title={isAdmin ? 'Unban (moves to Reserved)' : 'Admins only'}
                      className={
                        'px-2 py-1 rounded text-green-600 ' +
                        (isAdmin ? 'hover:bg-gray-100' : 'bg-gray-200 text-gray-400 cursor-not-allowed')
                      }
                    >
                      <UserCheck size={16} />
                    </button>

                    <button
                      onClick={() => openEdit(driver)}
                      className="px-2 py-1 rounded hover:bg-gray-100 text-blue-600"
                      title="View details"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showEdit && selectedBan && (
        <BannedDriverEditModal
          ban={selectedBan}
          onClose={closeEdit}
          onUpdate={handleBanUpdated}
        />
      )}
    </>
  );
}
